/**
 * ImageEditor list — `/prototypes/image-editor`
 *
 * Recent image edits. New edits are created from the asset library's per-asset
 * "Edit" action (admin-gated). Admin-gated via PrototypeLayout.
 *
 * @feature-spec brain/vidmax/feature-specs/image-editor.spec.md
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Badge, Card, CardContent } from "@aix/ui"

import CustomHead from "~/components/SEOHead/CustomHead"
import { PrototypeLayout } from "~/layouts/PrototypeLayout"
import { api } from "~/utils/api"

function ImageEditorList() {
  const navigate = useNavigate()
  const editsQuery = api.imageEditor.list.useQuery({ limit: 50 })

  return (
    <div className="mx-auto max-w-5xl p-6">
      <CustomHead title="Image Editor" />
      <div className="mb-4">
        <h1 className="text-xl font-semibold">Image Editor</h1>
        <p className="text-sm text-muted-foreground">
          Layer text, logo, and CTA over a generated image, then export a flattened asset. Start a new edit from the{" "}
          <strong>Edit</strong> action on any image in the asset library.
        </p>
      </div>

      {editsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (editsQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No edits yet. Open an image in the asset library and click Edit.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {(editsQuery.data ?? []).map((e) => (
            <Card
              key={e.id}
              className="cursor-pointer overflow-hidden transition hover:ring-2 hover:ring-primary"
              onClick={() => void navigate({ to: "/prototypes/image-editor/$imageEditId", params: { imageEditId: e.id } })}
            >
              <div className="aspect-square bg-muted">
                {(e.exportedUrl ?? e.sourceUrl) && (
                  <img src={e.exportedUrl ?? e.sourceUrl ?? ""} alt={e.name ?? "edit"} className="h-full w-full object-cover" />
                )}
              </div>
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <span className="truncate text-xs">{e.name ?? "Untitled"}</span>
                  <Badge variant="secondary" className="text-[10px]">{e.aspectRatio}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function Wrapper() {
  return (
    <PrototypeLayout>
      <ImageEditorList />
    </PrototypeLayout>
  )
}

export const Route = createFileRoute("/prototypes/image-editor/")({ component: Wrapper })
